import { autorun, makeObservable, runInAction, action, observable } from "mobx";

import type { ServiceAuth } from "./service-auth";
import { store } from "../../store";

export class ServiceSse {
    @observable accessor eventSource: EventSource | undefined;

    @observable accessor status: "disconnected" | "connecting" | "connected" = "connecting";

    private reconnectInterval: number | undefined;

    constructor(private readonly serviceAuth: ServiceAuth) {
        makeObservable(this);

        autorun(() => {
            if (!this.serviceAuth.isLoggedIn) {
                this.destroyEventSource();
                return;
            }

            if (this.status == "connecting") {
                this.createEventSource();
            }
        });
    }

    @action.bound
    private createEventSource(): void {
        if (this.eventSource) {
            this.destroyEventSource();
        }

        this.eventSource = new EventSource("/api/v1/sse/subscribe", { withCredentials: true });

        this.eventSource.onopen = this.handleEventSourceOpen;
        this.eventSource.onerror = this.handleEventSourceError;

        this.eventSource.addEventListener(
            "persistent-notification-created",
            this.handlePersistentNotificationCreatedOrUpdated,
        );
        this.eventSource.addEventListener(
            "persistent-notification-updated",
            this.handlePersistentNotificationCreatedOrUpdated,
        );
        this.eventSource.addEventListener(
            "persistent-notification-deleted",
            this.handlePersistentNotificationDeleted,
        );
    }

    @action.bound
    private destroyEventSource(): void {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = undefined;
            this.status = "disconnected";
        }
    }

    @action.bound
    private handleEventSourceOpen(_event: Event): void {
        runInAction(() => {
            this.status = "connected";

            if (this.reconnectInterval) {
                window.clearTimeout(this.reconnectInterval);
                this.reconnectInterval = undefined;
            }

            store.repositoryPersistentNotifications.reset();
            store.repositoryPersistentNotifications.fetchAll();
        });
    }

    @action.bound
    private handleEventSourceError(_event: Event): void {
        if (this.eventSource?.readyState != 2) {
            return;
        }

        runInAction(() => {
            this.destroyEventSource();

            if (!this.reconnectInterval) {
                this.reconnectInterval = window.setInterval(() => {
                    runInAction(() => {
                        this.status = "connecting";
                    });
                }, 60000);
            }
        });
    }

    @action.bound
    private handlePersistentNotificationCreatedOrUpdated(event: MessageEvent): void {
        runInAction(() => {
            store.repositoryPersistentNotifications.reloadId(event.data);
        });
    }

    @action.bound
    private handlePersistentNotificationDeleted(event: MessageEvent): void {
        runInAction(() => {
            store.repositoryPersistentNotifications.evict(event.data);
        });
    }
}
